import * as i0 from '@angular/core';
import { Injectable, NgModule, Optional, SkipSelf, Directive, Input, EventEmitter, Output } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpParams, HTTP_INTERCEPTORS } from '@angular/common/http';
import { map } from 'rxjs/operators';

/**
 *
 */
const appTag = {
    edition: 'edition',
    apiEdition: 'api-edition',
    antiForgeryToken: 'anti-forgery-token',
    moduleId: 'module-id',
    tabId: 'tab-id',
    contentBlockId: 'content-block-id',
    // new in 11.01
    angularPath: 'angular-path',
};

/**
 * Special service to inject in the root of the app.
 * Allows you to pass configuration on the app-tag in HTML to configure the application.
 *
 */
class AppTagService {
    constructor(
    /**
     * The app-tag on the HTML page.
     */
    appElement) {
        this.appElement = appElement;
    }
    /**
     * Get the value of an attribute on the main app-tag
     * @param attributeName
     * @returns
     */
    getAttribute(attributeName) {
        // todo: after upgrading to NG8, probably use el.GetAttribute
        return this.appElement.nativeElement.getAttribute(attributeName);
    }
}

const runtimeDefaults = {
    addHttpHeaders: true
};
let constructorCount = 0;
/**
 * The Context gives you things from DNN and 2sxc which matches the current runtime context.
 * So it auto-detects what's going on in the page and initializes / provides everything.
 *
 * Note: some properties like moduleId are probably not actually in use any more and will probably be empty
 *
 * @class Context
 * @implements {ContextInfo}
 */
class Context {
    constructor() {
        this.$2sxc = window.$2sxc;
        if (this.$2sxc === undefined) {
            throw new Error('window.$2sxc is null - you probably forgot to include the script before loading angular');
        }
        this.check2sxcVersion();
        constructorCount++;
        if (constructorCount > 1) {
            console.warn('The Context object of sxc-angular was created more than once. This is unexpected, and will probably lead to problems with the api calls.');
        }
    }
    check2sxcVersion() {
        // Actually the required version is 10.25.2, but 2sxc-ui reports 10.25.1 in 2sxc 10.25.2
        const requiredVersion = [10, 25, 1];
        const version = this.$2sxc.sysinfo.version.split('.').map((v) => parseInt(v));
        // Reduce version to comparision number - 0 means equal, 1 means version > requiredVersion, -1 means version < requiredVersion
        const compareVersions = requiredVersion.reduce((acc, _, i) => acc != 0 ? acc : Math.sign(version[i] - requiredVersion[i]), 0);
        if (compareVersions < 0) {
            throw new Error(`Installed 2sxc version is ${version.join('.')} but ${requiredVersion.join('.')} is required for sxc-angular.`);
        }
    }
    /**
     * Pre-Configure this context - can be used to configure values in a subclass
     * @param preConfig Pre-Configuration values for this context
     */
    preConfigure(preConfig) {
        this.preConfiguration = preConfig;
        return this;
    }
    /**
     * Configure 2sxc in the context of a HTMLNode.
     * @param htmlNode the HTMLNode
     */
    autoConfigure(htmlNode) {
        this.appTagService = new AppTagService(htmlNode);
        let settings = {
            ...runtimeDefaults,
            ...this.getContextFromAppTag(),
            ...this.preConfiguration
        };
        // Use pre-configured values already in settings if defined; otherwise
        // get from HTMLNode
        settings.sxc = settings.sxc ||
            (settings.moduleId
                ? this.$2sxc(settings.moduleId, settings.contentBlockId)
                : this.$2sxc(htmlNode.nativeElement));
        if (!settings.sxc) {
            throw new Error('couldn\'t get sxc instance - reason unknown');
        }
        this.sxc = settings.sxc;
        this.addHttpHeaders = settings.addHttpHeaders;
        this.appNameInPath = settings.appNameInPath;
        this.edition = settings.edition;
        this.apiEdition = settings.apiEdition;
        // new in 11.01 - change the base path for angular chunks if needed
        if (settings.angularPath) {
            this.angularPath = settings.angularPath;
            console.log('will set webpcak base to: ' + settings.angularPath);
            __webpack_public_path__ = settings.angularPath;
            console.log('done setting');
        }
    }
    /**
     * Get an attribute value from the app-tag
     * @param name attribute name
     */
    getAppAttribute(name) {
        return this.appTagService.getAttribute(name);
    }
    /**
     * Get context information like module-id from the app-root tag
     * new in Sxc-Angular 8
     */
    getContextFromAppTag() {
        let contextFromApp = {
            edition: this.appTagService.getAttribute(appTag.edition),
            apiEdition: this.appTagService.getAttribute(appTag.apiEdition),
            // 2021-02-26 2dm v11.01 added
            angularPath: this.appTagService.getAttribute(appTag.angularPath),
        };
        // Return an object containing only the not-null properties
        return contextFromApp;
    }
}
Context.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: Context, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
Context.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: Context, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: Context, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

/** @internal */
function getHttpParams(params) {
    return typeof (params) === 'string'
        ? new HttpParams({ fromString: params })
        : params instanceof HttpParams
            ? params
            : new HttpParams({ fromObject: params });
}

/** @internal */ const apiRouteName = 'api';
/** @internal */ const contentRouteName = 'content';
/** @internal */ const queryRouteName = 'query';
/** @internal */ const routeRoot = 'app/auto/';
/** @internal */ const routeApi = `${routeRoot}${apiRouteName}`;
/** @internal */ const routeQuery = `${routeRoot}${queryRouteName}`;
/** @internal */ const routeContent = `${routeRoot}${contentRouteName}`;

/**
 * Quickly call WebApis on the current App
 *
 * @export
 * @class SxcApiService
 */
class SxcApiService {
    constructor(http, controller) {
        this.http = http;
        this.controller = controller;
    }
    /**
     * Get the request URL of the api
     */
    url(method) {
        return `${routeApi}/${this.controller}/${method}`;
    }
    /**
     * Do a GET request to the specified 2sxc api controller
     */
    get(method, urlParams) {
        const requestParams = getHttpParams(urlParams);
        return this.http.get(this.url(method), { params: requestParams });
    }
    /**
     * Do a POST request to the specified 2sxc api controller
     */
    post(method, urlParams, body) {
        const requestParams = getHttpParams(urlParams);
        return this.http.post(this.url(method), body, { params: requestParams });
    }
    /**
     * Do a PUT request to the specified 2sxc api controller
     */
    put(method, urlParams, body) {
        const requestParams = getHttpParams(urlParams);
        return this.http.put(this.url(method), body, { params: requestParams });
    }
    /**
     * Do a DELETE request to the specified 2sxc api controller
     */
    delete(method, urlParams) {
        const requestParams = getHttpParams(urlParams);
        return this.http.put(this.url(method), { params: requestParams });
    }
}

/**
 * A helper to access data from 2sxc
 *
 * @class SxcDataService
 * @template T Type which the system will return
 */
class SxcDataService {
    constructor(http, contentType) {
        this.http = http;
        this.contentType = contentType;
    }
    /**
     * Get all items of this type
     */
    getAll() {
        const url = `${routeContent}/${this.contentType}`;
        return this.http.get(url);
    }
    /**
     * get the specific item with the ID
     */
    getOne(id) {
        const url = `${routeContent}/${this.contentType}/${id}`;
        return this.http.get(url);
    }
    /**
     * Internal implementation of create
     */
    create(item, metadataFor) {
        const url = `${routeContent}/${this.contentType}`;
        item = (metadataFor != null ? { ...item, For: metadataFor } : item);
        return this.http.post(url, item);
    }
    /**
     * Update the specific item with the ID to the item
     */
    update(id, item) {
        const url = `${routeContent}/${this.contentType}/${id}`;
        return this.http.post(url, item);
    }
    /**
     * Delete the specific item with ID/with GUID
     */
    delete(id) {
        const url = `${routeContent}/${this.contentType}/${id}`;
        return this.http.delete(url);
    }
}

/**
 * Query object for getting typed data.
 * @export
 * @class SxcQueryService
 * @template T type (usually an interface) of the data coming back
 */
class SxcQueryService {
    constructor(http, name) {
        this.http = http;
        this.name = name;
        this.streamParamKey = 'stream';
    }
    /**
     * Internal implementation of getAll
     */
    getAll(params, data) {
        const url = `${routeQuery}/${this.name}`;
        const streamParams = getHttpParams(params);
        if (data)
            return this.http.post(url, data, { params: streamParams });
        return this.http.get(url, { params: streamParams });
    }
    /**
     * Internal implementation of getStream
     */
    getStream(stream, urlParams, data) {
        const url = `${routeQuery}/${this.name}?${this.streamParamKey}=${stream}`;
        const streamParams = getHttpParams(urlParams);
        if (data)
            return this.http.post(url, data, { params: streamParams }).pipe(map(res => res[stream]));
        return this.http.get(url, { params: streamParams }).pipe(map(res => res[stream]));
    }
    /**
     * Internal implementation of getStreams
     */
    getStreams(streams, urlParams, data) {
        const url = `${routeQuery}/${this.name}?${this.streamParamKey}=${streams.join(',')}`;
        const streamParams = getHttpParams(urlParams);
        if (data)
            return this.http.post(url, data, { params: streamParams });
        return this.http.get(url, { params: streamParams });
    }
}

/**
 * 2sxc App Instance Object
 * <br>
 * gives you access to content and query streams using the content$ and query$ commands
 * you can also use the content and query managers, but these are currently not so useful.
 *
 * @export
 * @class SxcApp
 */
class SxcApp {
    constructor(http) {
        this.http = http;
    }
    /**
     * Cet a content manager object for a specific ContentType
     * @param contentType name of the content-type
     * @returns a query object with .getAll(), .getOne(), .create(), .update(), .delete()
     */
    data(contentType) {
        return new SxcDataService(this.http, contentType);
    }
    /**
     * get a query object to then start queries
     * @param name the query name
     * @returns a SxcQueryService object with .getAll(), .getStreams(), .getStream()
     */
    query(name) {
        return new SxcQueryService(this.http, name);
    }
    /**
     * get an api object to then start api-calls
     * @param controller the api controller
     * @returns an SxcApiService object with .url(), .get<T>(), .post<T>(), .put<T>(), .delete<T>() method
     */
    api(controller) {
        return new SxcApiService(this.http, controller);
    }
}
SxcApp.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcApp, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
SxcApp.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcApp, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcApp, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class SxcHttpInterceptor {
    constructor(context) {
        this.context = context;
    }
    intercept(req, next) {
        // skip interceptor for CORS requests
        if (!this.isSameOrigin(req))
            return next.handle(req);
        let url = req.url;
        let ctx = this.context;
        if (ctx.$2sxc) {
            url = ctx.$2sxc.http.apiUrl(req.url);
        }
        // change to use api of an edition, if an edition was specified
        // but only do this on api-routes, the others don't support editions
        if (ctx.apiEdition) {
            url = url.replace(routeApi, routeRoot + ctx.apiEdition + '/' + apiRouteName);
        }
        if (ctx.appNameInPath) {
            url = url.replace(routeRoot, `app/${ctx.appNameInPath}/`);
        }
        let headers = {};
        if (ctx.addHttpHeaders && ctx.sxc) {
            headers = ctx.sxc.webApi.headers();
            headers = this.convertAllPropertiesToString(headers);
        }
        // Clone the request and update the url with 2sxc params.
        const newReq = req.clone({
            url: url,
            setHeaders: headers,
        });
        return next.handle(newReq);
    }
    isSameOrigin(req) {
        let url = req.url.toLowerCase();
        let isRelativeUrl = true;
        if (url.startsWith('https://') || url.startsWith('http://')) {
            isRelativeUrl = false;
        }
        else if (url.startsWith('//')) {
            // protocol relative url
            isRelativeUrl = false;
            url = window.location.protocol + url;
        }
        if (isRelativeUrl)
            return true;
        if (url.startsWith(`${window.location.protocol}//${window.location.host}`))
            return true;
        return false;
    }
    convertAllPropertiesToString(obj) {
        return Object.keys(obj).reduce((a, k) => ({ ...a, [k]: obj[k].toString() }), {});
    }
}
SxcHttpInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcHttpInterceptor, deps: [{ token: Context }], target: i0.ɵɵFactoryTarget.Injectable });
SxcHttpInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcHttpInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcHttpInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: Context }]; } });

/**
 * Register the `SxcInterceptor` in a Module.
 * Happens automatically when you import the SxcRootModule.
 */
const SxcHttpInterceptorProvider = {
    provide: HTTP_INTERCEPTORS,
    useClass: SxcHttpInterceptor,
    multi: true
};

/**
 * The Root module which must be included in the application root.
 * It ensures that context etc. is singleton across the entire application
 * @export
 * @class SxcRootModule
 */
class SxcRootModule {
    // This constructor is a special helper to prevent use in sub-modules
    // https://angular.io/guide/singleton-services#prevent-reimport-of-the-greetingmodule
    constructor(parentModule) {
        if (parentModule)
            throw new Error('SxcRootModule is already loaded. Import it in the root AppModule only');
    }
}
SxcRootModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcRootModule, deps: [{ token: SxcRootModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule });
SxcRootModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcRootModule });
SxcRootModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcRootModule, providers: [
        SxcApp,
        Context,
        SxcHttpInterceptorProvider,
    ], imports: [[
        // Note: it's extremely important that HttpClientModule is _not_ imported
        // anywhere. Otherwise it will break sub-modules
        // because it will create a new HttpClientModule which won't have the interceptor
        // HttpClientModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcRootModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                    // Note: it's extremely important that HttpClientModule is _not_ imported
                    // anywhere. Otherwise it will break sub-modules
                    // because it will create a new HttpClientModule which won't have the interceptor
                    // HttpClientModule,
                    ],
                    providers: [
                        SxcApp,
                        Context,
                        SxcHttpInterceptorProvider,
                    ]
                }]
        }], ctorParameters: function () { return [{ type: SxcRootModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

class SxcToolbarDirective {
    constructor(elementRef, context) {
        this.elementRef = elementRef;
        this.context = context;
        this.config = {};
    }
    ngOnInit() {
        const sxc = this.context.sxc;
        if (!sxc.manage)
            return; // edit not available, probably not logged in
        this.elementRef.nativeElement.innerHTML = sxc.manage.getToolbar(this.config.toolbar, this.config.settings);
    }
}
SxcToolbarDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcToolbarDirective, deps: [{ token: i0.ElementRef }, { token: Context }], target: i0.ɵɵFactoryTarget.Directive });
SxcToolbarDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.0", type: SxcToolbarDirective, selector: "sxc-toolbar", inputs: { config: "config" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcToolbarDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'sxc-toolbar'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: Context }]; }, propDecorators: { config: [{
                type: Input
            }] } });

/**
 * The toolbar attribute to be used on any HTML tag.
 * Will bring the floating edit-toolbar to the UI if the user is logged in
 *
 * @export
 * @class SxcTagToolbarDirective
 * @implements {OnInit}
 */
class SxcTagToolbarDirective {
    constructor(element, context) {
        this.element = element;
        this.context = context;
        /**
         * A refresh callback when an action on the toolbar requires data to be refreshed.
         * If not specified, the page will simply reload, if specified, this action will run and page-reload won't happen.
         * New in v.11.12
         */
        this.refresh = new EventEmitter();
    }
    ngOnInit() {
        if (!this.context?.sxc?.isEditMode())
            return;
        const node = this.element.nativeElement;
        this.preventRefreshIfListenerConfigured();
        node.setAttribute("sxc-toolbar", JSON.stringify(this.sxcToolbar || {}));
        return this.context.$2sxc?._manage?._toolbarManager.build(node);
    }
    /**
     * Check if the event emiter has a listener, and if yes, stop automatic page reload
     */
    preventRefreshIfListenerConfigured() {
        //
        if (this.refresh.observers.length > 0) {
            this.element.nativeElement.addEventListener('toolbar-init', (event) => {
                event?.detail?.workflow?.add({
                    command: 'refresh',
                    code: (wfArgs) => {
                        this.refresh.emit(wfArgs); // emit event
                        return false; // prevent default refresh of the 2sxc API
                    }
                });
            });
        }
    }
}
SxcTagToolbarDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcTagToolbarDirective, deps: [{ token: i0.ElementRef }, { token: Context }], target: i0.ɵɵFactoryTarget.Directive });
SxcTagToolbarDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.0", type: SxcTagToolbarDirective, selector: "[sxc-toolbar]", inputs: { sxcToolbar: ["sxc-toolbar", "sxcToolbar"] }, outputs: { refresh: "refresh" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: SxcTagToolbarDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sxc-toolbar]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: Context }]; }, propDecorators: { sxcToolbar: [{
                type: Input,
                args: ['sxc-toolbar']
            }], refresh: [{
                type: Output,
                args: ['refresh']
            }] } });

/**
 * This module provides content-editing / management features to Angular.
 * It ensures that you can use attributes like sxc-toolbar
 * and tags like <sxc-toolbar>
 *
 * @export
 * @class ContentManagerModule
 */
class ContentManagerModule {
}
ContentManagerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: ContentManagerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ContentManagerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: ContentManagerModule, declarations: [SxcToolbarDirective,
        SxcTagToolbarDirective], exports: [SxcToolbarDirective,
        SxcTagToolbarDirective] });
ContentManagerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: ContentManagerModule, providers: [
        SxcApp,
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.0", ngImport: i0, type: ContentManagerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SxcToolbarDirective,
                        SxcTagToolbarDirective,
                    ],
                    providers: [
                        SxcApp,
                    ],
                    exports: [
                        SxcToolbarDirective,
                        SxcTagToolbarDirective,
                    ]
                }]
        }] });

/**
 * A root app component which initializes the context-providers once the app is loaded
 * This is the earliest moment we can access the ElementRef, because before that
 * it's not attached to the DOM, so auto-detect wouldn't work.
 * @export
 * @class SxcAppComponent
 */
class SxcAppComponent {
    /**
     *
     * @param element the angular ElementRef - required to auto-detect moduleId and more
     * @param context the context service, which handles and shares auto-detection
     * @param enableDefaultSubmit causes enter-hits to submit the asp.net forms (defaults to false)
     */
    constructor(element, context, enableDefaultSubmit) {
        // auto-config to pick up tab-id, module id, etc.
        context.autoConfigure(element);
        // prevent asp.net submit action caused by enter-keys inside our app
        if (!enableDefaultSubmit)
            element.nativeElement.addEventListener('keydown', (e) => {
                if (e.target.tagName.toLowerCase() === 'input' && e.keyCode === 13)
                    e.preventDefault();
            });
    }
}

/*
  Primary exports
  The exports here must be chosen wisely, because they should really only contain the publicly used / created / typed objects.
*/

/**
 * Generated bundle index. Do not edit.
 */

export { AppTagService, ContentManagerModule, Context, SxcApiService, SxcApp, SxcAppComponent, SxcDataService, SxcHttpInterceptorProvider, SxcQueryService, SxcRootModule, SxcTagToolbarDirective, SxcToolbarDirective };
